import { ApiFailure, getActiveOrg } from '@/lib/api';

/**
 * Reading a server-sent event stream from a POST.
 *
 * `EventSource` cannot do this: it only issues GETs, and the question is a body
 * — which is the usual reason a chat ends up hand-rolling the parser rather
 * than using the browser's.
 *
 * The format is small enough to read in one function: frames separated by a
 * blank line, each a set of `field: value` lines. Only `event` and `data` are
 * used, and a frame split across two network chunks is the normal case rather
 * than the exceptional one — which is why the buffer is only consumed up to the
 * last complete frame.
 */
export async function postEventStream(
  path: string,
  body: unknown,
  handlers: {
    onEvent: (event: string, data: unknown) => void;
    signal?: AbortSignal;
  },
): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  const org = getActiveOrg();
  if (org) headers['X-Org-Id'] = org;

  const res = await fetch(`/api${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    credentials: 'same-origin',
    signal: handlers.signal,
  });

  if (!res.ok || !res.body) {
    // Before the stream opens, a failure is still an ordinary HTTP failure and
    // is reported as one, so the form shows the same message it always did.
    const text = await res.text().catch(() => '');
    let code = 'request_failed';
    try {
      code = (JSON.parse(text) as { error?: string }).error ?? code;
    } catch {
      /* a body that is not JSON tells us nothing more than the status */
    }
    throw new ApiFailure(res.status, code);
  }

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    const { frames, rest } = readFrames(buffer + value);
    buffer = rest;
    for (const frame of frames) {
      try {
        handlers.onEvent(frame.event, JSON.parse(frame.data));
      } catch {
        // A frame we cannot read is dropped rather than taking the stream
        // down: the turn still settles on its `done` frame.
      }
    }
  }
}

export interface SseFrame {
  event: string;
  data: string;
}

/**
 * The complete frames in a buffer, and whatever is left over.
 *
 * Separated out because this is the part that is easy to get wrong and
 * impossible to see going wrong: a frame split across two network chunks is
 * the normal case, not the exceptional one, and consuming a partial frame
 * loses a word of the answer rather than raising anything.
 */
export function readFrames(buffer: string): { frames: SseFrame[]; rest: string } {
  const frames: SseFrame[] = [];
  let rest = buffer;

  for (;;) {
    const split = rest.indexOf('\n\n');
    if (split === -1) break;
    const block = rest.slice(0, split);
    rest = rest.slice(split + 2);

    let event = 'message';
    const data: string[] = [];
    for (const rawLine of block.split('\n')) {
      const line = rawLine.replace(/\r$/, '');
      if (line.startsWith('event:')) event = line.slice(6).trim();
      // A `data:` line keeps everything after one optional space, so a value
      // that itself begins with a space survives.
      else if (line.startsWith('data:')) data.push(line.slice(line[5] === ' ' ? 6 : 5));
    }
    if (data.length > 0) frames.push({ event, data: data.join('\n') });
  }

  return { frames, rest };
}
